/**
 * Storage Utilities
 *
 * Standalone module for localStorage operations with:
 * - Version management for state structure changes
 * - Error handling
 * - Type safety
 * - Easy clear/reset functionality
 *
 * Can be easily modified or replaced with sessionStorage,
 * IndexedDB, or any other storage solution.
 */

const STORAGE_VERSION = '1.0';
const STORAGE_PREFIX = 'miner_app_';

/**
 * Storage keys
 */
export const STORAGE_KEYS = {
    MINING_STATE: `${STORAGE_PREFIX}mining_state`,
    USER_PREFERENCES: `${STORAGE_PREFIX}user_preferences`,
    VERSION: `${STORAGE_PREFIX}version`,
};

/**
 * Save data to localStorage
 *
 * @param {string} key - Storage key
 * @param {any} data - Data to store (will be JSON stringified)
 * @returns {boolean} Success status
 */
export function saveToStorage(key, data) {
    try {
        const storageData = {
            version: STORAGE_VERSION,
            timestamp: Date.now(),
            data,
        };
        localStorage.setItem(key, JSON.stringify(storageData));
        return true;
    } catch (error) {
        console.error('Error saving to localStorage:', error);
        return false;
    }
}

/**
 * Load data from localStorage
 *
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found
 * @returns {any} Stored data or default value
 */
export function loadFromStorage(key, defaultValue = null) {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) {
            return defaultValue;
        }

        const storageData = JSON.parse(stored);

        // Check version compatibility
        if (storageData.version !== STORAGE_VERSION) {
            console.warn(`Storage version mismatch for ${key}. Clearing old data.`);
            removeFromStorage(key);
            return defaultValue;
        }

        return storageData.data;
    } catch (error) {
        console.error('Error loading from localStorage:', error);
        return defaultValue;
    }
}

/**
 * Remove item from localStorage
 *
 * @param {string} key - Storage key
 */
export function removeFromStorage(key) {
    try {
        localStorage.removeItem(key);
        return true;
    } catch (error) {
        console.error('Error removing from localStorage:', error);
        return false;
    }
}

/**
 * Clear all app data from localStorage
 */
export function clearAllStorage() {
    try {
        Object.values(STORAGE_KEYS).forEach(key => {
            localStorage.removeItem(key);
        });
        return true;
    } catch (error) {
        console.error('Error clearing localStorage:', error);
        return false;
    }
}

/**
 * Check if localStorage is available
 *
 * @returns {boolean} True if localStorage is available
 */
export function isStorageAvailable() {
    try {
        const test = '__storage_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (error) {
        return false;
    }
}

/**
 * Get storage size (approximate)
 *
 * @returns {number} Approximate size in bytes
 */
export function getStorageSize() {
    try {
        let total = 0;
        for (const key in localStorage) {
            if (localStorage.hasOwnProperty(key)) {
                total += localStorage[key].length + key.length;
            }
        }
        return total;
    } catch (error) {
        return 0;
    }
}

/**
 * Save mining state to localStorage
 *
 * @param {Object} miningState - Mining state to save
 */
export function saveMiningState(miningState) {
    return saveToStorage(STORAGE_KEYS.MINING_STATE, miningState);
}

/**
 * Load mining state from localStorage
 *
 * @param {Object} defaultState - Default state if not found
 * @returns {Object} Mining state
 */
export function loadMiningState(defaultState) {
    return loadFromStorage(STORAGE_KEYS.MINING_STATE, defaultState);
}

/**
 * Save user preferences
 *
 * @param {Object} preferences - User preferences
 */
export function saveUserPreferences(preferences) {
    return saveToStorage(STORAGE_KEYS.USER_PREFERENCES, preferences);
}

/**
 * Load user preferences
 *
 * @param {Object} defaultPreferences - Default preferences
 * @returns {Object} User preferences
 */
export function loadUserPreferences(defaultPreferences = {}) {
    return loadFromStorage(STORAGE_KEYS.USER_PREFERENCES, defaultPreferences);
}
